import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { EXPIRE_MODE_DAYS, type LinkExpireMode } from '../model';

const EXPIRE_MODE_SECONDS: Record<LinkExpireMode, number> = {
  never: -1,
  '7days': 604800,
  '30days': 2592000,
  '60days': 5184000,
  '90days': 7776000,
};

/**
 * LinkExpireMode에서 만료 시간(초) 계산
 * @returns 만료까지 남은 초, never인 경우 -1
 */
export function calculateExpireSeconds(
  mode: LinkExpireMode,
  enabled: boolean = true,
): number {
  if (!enabled) {
    return -1;
  }
  return EXPIRE_MODE_SECONDS[mode];
}

/**
 * 만료 날짜 계산
 * @param mode - 만료 모드
 * @param baseDate - 계산 기준 날짜 (UTC string), 기본값은 현재 시간
 * @returns 만료 날짜 (Dayjs)
 */
export function getExpireDate(mode: LinkExpireMode, baseDate?: string) {
  const days = EXPIRE_MODE_DAYS[mode];
  const base = baseDate ? getTimezoneDayjs(baseDate) : getTimezoneDayjs();
  return base.add(days, 'day');
}

/**
 * 만료 날짜를 포맷팅하여 반환
 * @param mode - 만료 모드
 * @param updateAt - 계산 기준 날짜 (UTC string)
 * @returns "Expires Jun 24, 05:04 PM" 형식의 문자열
 */
export function formatExpireDate(
  mode: LinkExpireMode,
  updateAt?: string,
): string {
  const expireDate = getExpireDate(mode, updateAt);
  return `Expires ${expireDate.format(DATE_FORMAT.readableHour)}`;
}

/**
 * 공유 링크 URL 생성
 */
export function buildSharedLinkUrl(linkId: string): string {
  return `${window.location.origin}/shared-link/${linkId}`;
}

const SECONDS_TO_MODE: Record<number, LinkExpireMode> = {
  604800: '7days',
  2592000: '30days',
  5184000: '60days',
  7776000: '90days',
};

/**
 * 만료 시간(초)을 LinkExpireMode로 변환
 * @param expireSeconds - 만료 시간(초) (604800=7일, 2592000=30일, 5184000=60일, 7776000=90일), 0은 비공개, -1은 무제한
 * @returns 만료 시간에 해당하는 LinkExpireMode
 */
export function expireSecondsToMode(expireSeconds: number): {
  mode: LinkExpireMode;
  enabled: boolean;
} {
  if (expireSeconds === -1 || expireSeconds === 0) {
    return { mode: '7days', enabled: false };
  }

  const mode = SECONDS_TO_MODE[expireSeconds];
  if (mode) {
    return { mode, enabled: true };
  }

  // 기본값으로 fallback
  return { mode: '7days', enabled: true };
}
